import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  PButton,
  PCheckbox,
  PPagination,
  PTable,
  PTableBody,
  PTableCell,
  PTableHead,
  PTableHeadCell,
  PTableHeadRow,
  PTableRow,
} from '@porsche-design-system/components-react';
import {
  CellContext,
  CellData,
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  RowData,
  RowSelectionState,
  SortingState,
  TableFeatures,
  flexRender,
  useTable,
} from '@tanstack/react-table';
import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  ActionGroup,
  Popover,
  Spacer,
  TableCellLoader,
  TableHeadFilter,
  TableHeadSortButton,
} from '../';
import { ColumnFilter } from './Filter';
import { dataTableFeatures } from './table-features';

declare module '@tanstack/react-table' {
  interface ColumnMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
    TValue extends CellData = CellData,
  > {
    name?: string;
    filterType?:
      | 'text'
      | 'date'
      | 'select'
      | 'organization'
      | 'numberRange'
      | 'number';
    filterOptions?: { label: string; value: string | boolean }[] | 'auto';
    filterOptionsTransformer?: (
      values: (string | boolean)[],
    ) => (string | boolean)[];
    filterOptionsKey?: string; // the key of the filter option if it's an object and filterOptions = auto
    testId?: string;
    onClick?: (data: TData) => void;
  }
}

interface DataTableProps<TData extends RowData> {
  caption: string;
  data: TData[];
  columns: ColumnDef<typeof dataTableFeatures, TData, any>[];
  defaultColumn?: Partial<ColumnDef<typeof dataTableFeatures, TData, unknown>>;
  idAccessor: keyof TData | ((d: TData) => string);
  isLoading?: boolean;
  i18n: {
    filterLabel: (columnName: string) => string;
    optionAll: string;
    buttonReset: string;
    buttonFilter: string;
    actionSearch: string;
    noData: string;
    customizeTable?: string;
    minLabel: string;
    maxLabel: string;
  };
  actions?: {
    top?: React.ReactNode;
    bottom?: React.ReactNode;
  };
  lastRowConfig?: { id: string; cell: () => React.ReactNode }[];
  isSelectable?: boolean;
  onSelect?: (selectedIds: string[]) => void;
  preSelectedItemIds?: string[];
  disabledSelectedItemIds?: string[];
  pagination?: {
    active?: boolean;
    pageSize?: number;
    pageIndex?: number;
    position?: 'both' | 'bottom';
  };
  defaultSort?: SortingState;
  sort?: { field: string; order: string };
  onSort?: (sort: { field: string; order: 'asc' | 'desc' | '' }) => void;
  isColumnsCustomizable?: boolean;
  initialColumnVisibility?: Record<string, boolean>;
  initialColumnFilters?: ColumnFiltersState;
  isMultiRowSelectable?: boolean;
  onColumnFiltersChange?: (filter: ColumnFiltersState) => void;
  onSortingChange?: (sort: SortingState) => void;
  onPaginationChange?: OnChangeFn<PaginationState>;
}

export const DataTable = <TData extends RowData>({
  caption,
  data,
  columns,
  defaultColumn,
  isLoading,
  actions,
  lastRowConfig,
  i18n,
  idAccessor,
  isSelectable = false,
  isColumnsCustomizable = false,
  isMultiRowSelectable,
  initialColumnVisibility = {},
  preSelectedItemIds = [],
  disabledSelectedItemIds = [],
  defaultSort = [],
  onSelect = (_) => _,
  onSort,
  sort,
  pagination,
  initialColumnFilters,
  onColumnFiltersChange,
  onSortingChange,
  onPaginationChange,
  ...props
}: DataTableProps<TData>) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialColumnFilters ?? [],
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    mapIdToIndex(data, preSelectedItemIds, idAccessor),
  );
  useEffect(() => {
    if (!isLoading) {
      // fixup initial selection after data is loaded
      setRowSelection(mapIdToIndex(data, preSelectedItemIds, idAccessor));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
  const {
    active: isPaginationActive = true,
    position: paginationPosition = 'both',
    pageSize: paginationPageSize = 10,
    pageIndex: paginationPageIndex = 0,
  } = pagination || {};

  const [sorting, setSorting] = useState<SortingState>(defaultSort);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: paginationPageIndex,
    pageSize: paginationPageSize,
  });
  const paginationState = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(initialColumnVisibility);
  const [isCustomizeTableVisible, setIsCustomizeTableVisible] = useState(false);

  useEffect(() => {
    onSelect?.(mapIndexToId(data, rowSelection, idAccessor));
  }, [data, idAccessor, onSelect, rowSelection]);

  useEffect(() => {
    const newOrder = sorting[0] ? (sorting[0]?.desc ? 'desc' : 'asc') : '';
    if ((sorting[0]?.id || '') != sort?.field || newOrder != sort?.order) {
      onSort?.({
        field: sorting[0]?.id || '',
        order: newOrder,
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting[0]?.id, sorting[0]?.desc]);

  useEffect(() => {
    const current = pageIndex + 1;
    const total = Math.ceil(data.length / pageSize);

    if (current > total) {
      // Reset page manually, because data was changed and current page is greater-than total count.
      // This is necessary, because we set autoResetPageIndex to false
      setPagination((value) => ({
        ...value,
        pageIndex: 0,
      }));
    }
    onPaginationChange?.({ pageIndex, pageSize });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, pageIndex, pageSize]);

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns: [
      ...(isSelectable
        ? [
            {
              id: 'selectColumn',
              size: 20,
              cell: ({
                row,
              }: CellContext<typeof dataTableFeatures, TData, unknown>) => (
                <PCheckbox
                  hideLabel
                  name={`select-${row.id}`}
                  checked={row.getIsSelected()}
                  disabled={!row.getCanSelect()}
                  onChange={(event) => {
                    const { checked } = event.target as HTMLInputElement;
                    row.getToggleSelectedHandler()({
                      target: { checked },
                    });
                  }}
                />
              ),
            },
          ]
        : []),
      ...columns,
    ],
    defaultColumn,
    state: {
      sorting,
      pagination: paginationState,
      columnFilters,
      ...(isSelectable && { rowSelection }),
      columnVisibility,
    },
    manualSorting: !!sort,
    manualPagination: !isPaginationActive,
    autoResetPageIndex: false,
    enableMultiRowSelection: isMultiRowSelectable,
    enableRowSelection: (row) => {
      const id = String(
        typeof idAccessor === 'function'
          ? idAccessor(row.original)
          : row.original[idAccessor],
      );
      return isSelectable && !disabledSelectedItemIds?.includes(id);
    },
    onRowSelectionChange: setRowSelection,
    onSortingChange: (value) => {
      const sortVal = typeof value === 'function' ? value(sorting) : value;
      onSortingChange?.(sortVal);
      setSorting(value);
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: (filter) => {
      // Reset page index manually, because we set autoResetPageIndex to false
      setPagination((value) => ({
        ...value,
        pageIndex: 0,
      }));

      const filterVal =
        typeof filter === 'function' ? filter(columnFilters) : filter;

      onColumnFiltersChange?.(filterVal);
      setColumnFilters(filter);
    },
    onColumnVisibilityChange: setColumnVisibility,
  });

  const showTopPagination = isPaginationActive && paginationPosition === 'both';
  const showTopAction = isColumnsCustomizable || actions?.top;

  const customizeTableButton = isColumnsCustomizable ? (
    <Popover
      open={isCustomizeTableVisible}
      onChange={(open) => setIsCustomizeTableVisible(open)}
      target={
        <PButton
          onClick={() => setIsCustomizeTableVisible((open) => !open)}
          icon={`arrow-head-${isCustomizeTableVisible ? 'up' : 'down'}`}
        >
          {i18n.customizeTable}
        </PButton>
      }
      placement="bottom-start"
    >
      {table.getAllLeafColumns().map((column) => (
        <Fragment key={column.id}>
          <PCheckbox
            label={column.columnDef.header as string}
            aria-label={column.columnDef.header as string}
            name={column.id}
            checked={column.getIsVisible()}
            onChange={(event) => {
              const { checked } = event.target as HTMLInputElement;
              column.getToggleVisibilityHandler()({
                target: { checked },
              });
              // checkbox state toggled via handler; checked is available if needed
            }}
          />

          <Spacer pb="md" />
        </Fragment>
      ))}
    </Popover>
  ) : null;

  return (
    <>
      {showTopAction || showTopPagination ? (
        <>
          <ActionGroup>
            {showTopAction ? (
              <>
                {isColumnsCustomizable && actions?.top ? (
                  <ActionGroup spacing="none">
                    {customizeTableButton}
                    {actions.top}
                  </ActionGroup>
                ) : null}

                {isColumnsCustomizable && !actions?.top
                  ? customizeTableButton
                  : null}

                {!isColumnsCustomizable && actions?.top ? actions.top : null}
              </>
            ) : (
              <div />
            )}

            {showTopPagination && !isLoading && !!data?.length ? (
              <PPagination
                totalItemsCount={table.getPageCount() * paginationPageSize}
                itemsPerPage={paginationPageSize}
                activePage={table.state.pagination.pageIndex + 1}
                onUpdate={(event) => {
                  table.setPageIndex(event.detail.page - 1);
                }}
              />
            ) : null}
          </ActionGroup>

          <Spacer mb="md" />
        </>
      ) : null}

      <PTable
        role="table"
        data-testid={isLoading ? 'table-loading' : 'table-ready'}
        caption={caption}
        {...props}
      >
        <PTableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <PTableHeadRow key={headerGroup.id}>
              {headerGroup.headers.map((header, i) => {
                const isSorted = header.column.getIsSorted();

                return (
                  <PTableHeadCell
                    key={`${header.id}_${i}`}
                    style={{
                      width: `${
                        header.column.id === 'selectColumn' ? '20px' : 'auto'
                      }`,
                      minWidth: header.column.getSize(),
                    }}
                  >
                    {header.column.getCanSort() ? (
                      <TableHeadSortButton
                        onClick={header.column.getToggleSortingHandler()}
                        direction={
                          isSorted ||
                          header.column.getNextSortingOrder() ||
                          undefined
                        }
                        isActive={!!isSorted}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHeadSortButton>
                    ) : (
                      <>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </>
                    )}

                    {header.column.getCanFilter() ? (
                      <TableHeadFilter>
                        <ColumnFilter
                          column={header.column}
                          i18n={{
                            ...i18n,
                            filterLabel: i18n.filterLabel(
                              header.column.columnDef.meta?.name ??
                                header.column.id,
                            ),
                          }}
                        />
                      </TableHeadFilter>
                    ) : null}
                  </PTableHeadCell>
                );
              })}
            </PTableHeadRow>
          ))}
        </PTableHead>
        <PTableBody>
          {!isLoading
            ? table.getRowModel().rows.map((row) => (
                <PTableRow role="row" key={row.id}>
                  {row.getVisibleCells().map((cell, i) => {
                    const testId = cell.column.columnDef.meta?.testId;
                    const onClick = cell.column.columnDef.meta?.onClick;

                    return (
                      <PTableCell
                        role="cell"
                        {...(onClick && {
                          onClick: () => onClick(row.original),
                        })}
                        key={`${cell.id}_${i}`}
                        style={{
                          minWidth: cell.column.getSize(),
                          ...(!cell.getIsPlaceholder() &&
                            (cell.column.columnDef.maxSize ?? 0) > 0 && {
                              maxWidth: cell.column.columnDef.maxSize,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }),
                          ...(onClick && { cursor: 'pointer' }),
                        }}
                        {...(testId && { 'data-testId': testId })}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </PTableCell>
                    );
                  })}
                </PTableRow>
              ))
            : null}

          {isLoading
            ? Array.from({ length: 3 }).map((_, index) => (
                <PTableRow key={`TableCellLoader_${index}`}>
                  {table.getAllColumns().map((col) => (
                    <PTableCell key={col.id}>
                      <TableCellLoader />
                    </PTableCell>
                  ))}
                </PTableRow>
              ))
            : null}

          {!isLoading && lastRowConfig && !!data.length ? (
            <PTableRow>
              {table.getVisibleFlatColumns().map((c) => {
                const config = lastRowConfig.find((r) => r.id === c.id);

                if (config) {
                  return <PTableCell key={c.id}>{config.cell()}</PTableCell>;
                }

                return <PTableCell key={c.id} />;
              })}
            </PTableRow>
          ) : null}
        </PTableBody>
      </PTable>

      {!isLoading && !data.length ? (
        <>
          <Center>{i18n.noData}</Center>
          <Spacer mb="md" />
        </>
      ) : null}

      {isPaginationActive || actions?.bottom ? (
        <ActionGroup>
          {actions?.bottom ? actions.bottom : <div />}
          {isPaginationActive && !isLoading && !!data?.length ? (
            <PPagination
              totalItemsCount={table.getPageCount() * paginationPageSize}
              itemsPerPage={paginationPageSize}
              activePage={table.state.pagination.pageIndex + 1}
              onUpdate={(event) => {
                table.setPageIndex(event.detail.page - 1);
              }}
            />
          ) : null}
        </ActionGroup>
      ) : null}
    </>
  );
};

// This function is only necessary for the row selection feature because react-table uses the row index
// to mark the selected rows.
const mapIdToIndex = <TData,>(
  data: TData[],
  ids: string[],
  idAccessor: keyof TData | ((d: TData) => string),
) => {
  return ids.reduce((acc, id) => {
    const index = data.findIndex((item) => {
      const itemId = String(
        typeof idAccessor === 'function' ? idAccessor(item) : item[idAccessor],
      );
      return itemId === id;
    });
    if (index !== -1) {
      acc[index] = true;
    }
    return acc;
  }, {} as RowSelectionState);
};

// Map back to the ids for the onSelect callback
const mapIndexToId = <TData,>(
  data: TData[],
  rowSelection: RowSelectionState,
  idAccessor: keyof TData | ((d: TData) => string),
) => {
  return Object.entries(rowSelection).map(([index]) => {
    const item = data[+index];
    return String(
      typeof idAccessor === 'function' ? idAccessor(item) : item[idAccessor],
    );
  });
};

const Center = styled('div', {
  base: { display: 'flex', justifyContent: 'center' },
});
